import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Radio, AlertCircle } from 'lucide-react';
import { useMQTT } from '../../context/MQTTContext';
import { useNavigate } from 'react-router-dom';

export default function GPSLiveCard({ wide = false }) {
  const { sensorData, geoStatus } = useMQTT();
  const navigate = useNavigate();
  const gps = sensorData.gps;

  const hasFix =
    Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude) &&
    (gps.latitude !== 0 || gps.longitude !== 0);

  // Bounding box for the mini map preview — zooms tighter as accuracy improves
  const span = useMemo(() => {
    const accDeg = gps.accuracy ? (gps.accuracy / 111000) * 4 : 0.008;
    return Math.min(Math.max(accDeg, 0.0015), 0.04);
  }, [gps.accuracy]);

  const mapUrl = useMemo(() => {
    if (!hasFix) return null;
    const bbox = [gps.longitude - span, gps.latitude - span,
                  gps.longitude + span, gps.latitude + span].join(',');
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${gps.latitude},${gps.longitude}`;
  }, [hasFix, gps.latitude, gps.longitude, span]);

  const live = geoStatus === 'live';
  const statusFg = live ? '#10b981' : geoStatus === 'locating' ? '#f59e0b' : '#ef4444';
  const statusLabel =
    live ? 'Live' :
    geoStatus === 'locating' ? 'Locating' :
    geoStatus === 'denied' ? 'Denied' : 'Offline';

  const speedKmh = Number.isFinite(gps.speed) ? gps.speed * 3.6 : null;

  return (
    <motion.div
      layout
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.985 }}
      onClick={() => navigate('/gps')}
      className={`relative overflow-hidden card-surface cursor-pointer ring-1 ring-emerald-500/30
        shadow-[0_0_28px_-4px_rgba(16,185,129,0.45)] ${wide ? 'sm:col-span-2 lg:col-span-2' : ''}`}
    >
      {/* Mini map preview */}
      <div className="relative h-32 sm:h-36 w-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        {mapUrl ? (
          <iframe
            title="gps-mini-map"
            src={mapUrl}
            className="w-full h-full border-0 pointer-events-none"
            scrolling="no"
          />
        ) : (
          <div className="w-full h-full grid place-items-center opacity-60">
            <MapPin className="w-8 h-8" />
          </div>
        )}

        {/* Marker overlay */}
        {hasFix && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white shadow-glow">
              <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-pulse-soft" />
              <Navigation
                className="relative w-5 h-5"
                style={{
                  transform: Number.isFinite(gps.heading)
                    ? `rotate(${gps.heading}deg)` : undefined,
                  transition: 'transform 0.4s ease'
                }}
              />
            </div>
          </div>
        )}

        {/* Live chip */}
        <div className="absolute top-2 left-2">
          <span
            className="chip backdrop-blur"
            style={{ background: `${statusFg}33`, color: statusFg }}
          >
            {live ? <Radio className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {statusLabel}
          </span>
        </div>

        {/* Open map hint */}
        <div className="absolute top-2 right-2">
          <span className="chip bg-black/40 text-white backdrop-blur">Open map →</span>
        </div>
      </div>

      {/* Coords + metrics */}
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-xl grid place-items-center text-white shadow-glow shrink-0"
                  style={{ backgroundImage: 'linear-gradient(135deg,#10b981,#059669)' }}>
              <MapPin className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-bold opacity-60">Live Location</p>
              <p className="font-extrabold tabular-nums truncate">
                {hasFix
                  ? `${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}`
                  : '— , —'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] py-2">
            <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Accuracy</p>
            <p className="text-sm font-bold tabular-nums mt-0.5">
              {gps.accuracy ? `±${gps.accuracy.toFixed(0)}m` : '—'}
            </p>
          </div>
          <div className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] py-2">
            <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Speed</p>
            <p className="text-sm font-bold tabular-nums mt-0.5">
              {speedKmh != null && speedKmh > 0.5
                ? `${speedKmh.toFixed(1)} km/h` : '—'}
            </p>
          </div>
          <div className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] py-2">
            <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Updated</p>
            <p className="text-sm font-bold tabular-nums mt-0.5">
              {gps.updatedAt ? new Date(gps.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
